type JsonValue = string | number | boolean | null | JsonObject | JsonArray;

interface JsonObject {
  [key: string]: JsonValue;
}

type JsonArray = JsonValue[];

function todosDigitosIguais(valor: string): boolean {
  return /^(\d)\1+$/.test(valor);
}

function cpfValido(cpf: string): boolean {
  if (cpf.length !== 11 || todosDigitosIguais(cpf)) return false;

  const calcularDigito = (base: string, pesoInicial: number): number => {
    const soma = base
      .split('')
      .reduce((total, digito, index) => total + Number(digito) * (pesoInicial - index), 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  return calcularDigito(cpf.slice(0, 9), 10) === Number(cpf[9])
    && calcularDigito(cpf.slice(0, 10), 11) === Number(cpf[10]);
}

function cnpjValido(cnpj: string): boolean {
  if (cnpj.length !== 14 || todosDigitosIguais(cnpj)) return false;

  const calcularDigito = (base: string, pesos: number[]): number => {
    const soma = base
      .split('')
      .reduce((total, digito, index) => total + Number(digito) * pesos[index], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const primeiroDigito = calcularDigito(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const segundoDigito = calcularDigito(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

  return primeiroDigito === Number(cnpj[12]) && segundoDigito === Number(cnpj[13]);
}

function tamanhoDocumentoCpfCnpj(digitos: string): number {
  if (digitos.length <= 11) return 11;

  if (
    digitos.length === 14 &&
    digitos.startsWith('000') &&
    !cnpjValido(digitos) &&
    cpfValido(digitos.slice(-11))
  ) {
    return 11;
  }

  return 14;
}

export function normalizarDocumentoCpfCnpj(valor?: string | number | null): string | undefined {
  const digitos = String(valor ?? '').replace(/\D/g, '');

  if (!digitos) return undefined;

  const tamanho = tamanhoDocumentoCpfCnpj(digitos);
  return digitos.padStart(tamanho, '0').slice(-tamanho);
}

export function FormatarCpfCnpj<T extends JsonObject>(obj: T | string): T {
  const camposDeBusca = ["cpf", "cnpj", "identificacaocliente"];

  const objeto =
        typeof obj === 'object'
            ? JSON.parse(JSON.stringify(obj))
            : typeof obj === 'string' ? JSON.parse(obj) : obj;

  if (typeof objeto !== 'object') return objeto;

  function determinarTamanhoDocumento(chave: string, valor: JsonValue): number {
    const chaveLower = chave.toLowerCase();
    const digitos = String(valor).replace(/\D/g, '');
    const camposCpfOuCnpj = [
      'cpfterceiro',
      'cpftitular',
      'cpflocador',
      'cpfgarantidor',
      'cpfcnpj',
      'identificacaocliente'
    ];
    const isCampoCpfOuCnpj = camposCpfOuCnpj.some((campo) => chaveLower.includes(campo));

    if (isCampoCpfOuCnpj) {
      return tamanhoDocumentoCpfCnpj(digitos);
    }

    // Campo exclusivamente de CNPJ (contém "cnpj" mas NÃO contém "cpf")
    // Ex: "cnpj", "cnpjParceiro"
    if (chaveLower.includes('cnpj') && !chaveLower.includes('cpf')) {
      return 14;
    }

    // Campo exclusivamente de CPF (contém "cpf" mas NÃO contém "cnpj")
    // Ex: "cpf", "cpfRepresentante"
    if (chaveLower.includes('cpf') && !chaveLower.includes('cnpj') && !isCampoCpfOuCnpj) {
      return 11;
    }

    // Campo genérico que pode ser CPF ou CNPJ (contém ambos, ex: "cpfCnpj", "cpfCnpjRepresentante")
    // ou campo "identificacaocliente"
    // Decide pelo tamanho dos dígitos:
    // - Até 11 dígitos → CPF (mesmo que comece com 0, ex: 01234567890)
    // - Acima de 11 dígitos → CNPJ
    return tamanhoDocumentoCpfCnpj(digitos);
  }

  function percorrer(valor: JsonValue): void {
    if (Array.isArray(valor)) {
      valor.forEach(percorrer);
      return;
    }

    if (valor && typeof valor === "object") {
      const registro = valor as JsonObject;

      for (const chave in registro) {
        const valorAtual = registro[chave];

        if (
          camposDeBusca.some((campo) => chave.toLowerCase().includes(campo)) &&
          ["string", "number"].includes(typeof registro[chave]) &&
          String(valorAtual).trim() !== ""
        ) {
          const tamanho = determinarTamanhoDocumento(chave, valorAtual);
          const digitos = String(valorAtual).replace(/\D/g, '');
          registro[chave] = digitos.padStart(tamanho, '0').slice(-tamanho);
        } else {
          percorrer(valorAtual);
        }
      }
    }
  }

  percorrer(objeto);

  return objeto;
}
